import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

type GetOneResult = Awaited<ReturnType<typeof nuxtPropertiesRepository.getOne>>;

// Intent prefetch of the reel page's property payload: a hover or click on a
// shorts card can start the getOne request before navigation, and the page's
// useAsyncData consumes the in-flight promise instead of re-requesting.
// Client-only module cache (SSR no-op), short TTL, capped, error results
// self-evict — any mismatch just falls back to a normal fetch.
const PREFETCH_TTL_MS = 30_000;
const PREFETCH_MAX_ENTRIES = 20;
const propertyPrefetch = new Map<
	string,
	{ promise: Promise<GetOneResult>; ts: number }
>();

export function consumePropertyIntentPrefetch(
	commercialId: string
): Promise<GetOneResult> | null {
	if (!import.meta.client) return null;
	const entry = propertyPrefetch.get(commercialId);
	if (!entry) return null;
	propertyPrefetch.delete(commercialId);
	if (Date.now() - entry.ts > PREFETCH_TTL_MS) return null;
	return entry.promise;
}

export function usePropertyIntentPrefetch() {
	const userSession = useUserSession();
	const getCookieData = useCookieData();

	function prefetchPropertyOnIntent(
		commercialId: string | null | undefined
	): void {
		if (!import.meta.client || !commercialId) return;

		const existing = propertyPrefetch.get(commercialId);
		if (existing && Date.now() - existing.ts <= PREFETCH_TTL_MS) return;

		if (propertyPrefetch.size >= PREFETCH_MAX_ENTRIES) {
			const oldest = propertyPrefetch.keys().next().value;
			if (oldest !== undefined) propertyPrefetch.delete(oldest);
		}

		const promise = nuxtPropertiesRepository
			.getOne(
				commercialId,
				userSession.user.value?.id ?? undefined,
				getCookieData()
			)
			.then(result => {
				const [property, error] = result;
				if (error || !property) propertyPrefetch.delete(commercialId);
				return result;
			})
			.catch(error => {
				propertyPrefetch.delete(commercialId);
				throw error;
			});
		propertyPrefetch.set(commercialId, { promise, ts: Date.now() });
	}

	return { prefetchPropertyOnIntent };
}
